import db from '../db';
import { contentHash } from '../db/contentHash';
import { getLatestBriefingConditional } from './dailyBriefing';
import { getBriefingConditional, getTopicDigestConditional } from './topics';
import { getArticleConditional } from './articles';
import { getAllHighlightsConditional } from './highlights';
import { RECENT_OPENED_LIMIT } from '../db/freshness';

// Internal: bounded async worker pool (concurrency n).
async function mapWithConcurrency(n, items, fn, { signal } = {}) {
  let i = 0;
  const workers = Array.from({ length: Math.min(n, items.length) }, async () => {
    while (i < items.length) {
      if (signal?.aborted) return;
      const idx = i++;
      await fn(items[idx], idx);
    }
  });
  await Promise.all(workers);
}

// Internal: collect article IDs referenced by daily + briefing + per-topic digest payloads,
// union with recently opened articles.
async function collectArticleIds(dailyPayload, briefingPayload, topicDigestPayloads = []) {
  const ids = new Set();
  (dailyPayload?.themes || []).forEach(t => {
    (t.whats_new || []).forEach(x => x.source_article_id && ids.add(x.source_article_id));
    (t.worth_saving || []).forEach(x => x.source_article_id && ids.add(x.source_article_id));
  });
  (briefingPayload?.digests || []).forEach(d => {
    (d.whats_new || []).forEach(x => x.article_id && ids.add(x.article_id));
    (d.recommended_reads || []).forEach(x => x.article_id && ids.add(x.article_id));
  });
  topicDigestPayloads.forEach(d => {
    if (!d) return;
    (d.whats_new || []).forEach(x => x.article_id && ids.add(x.article_id));
    (d.recommended_reads || []).forEach(x => x.article_id && ids.add(x.article_id));
  });
  const recent = await db.articles
    .orderBy('last_opened_at').reverse().limit(RECENT_OPENED_LIMIT).primaryKeys();
  recent.forEach(id => ids.add(id));
  return ids;
}

async function fetchAndStoreDaily() {
  const cached = await db.daily_briefings.get('latest');
  const result = await getLatestBriefingConditional({ etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.daily_briefings.update('latest', { fetched_at: now });
    return cached?.payload ?? null;
  }
  // payload === null means 404 (no briefing exists yet) — skip write.
  if (result.payload === null) return null;
  const hash = await contentHash(result.payload);
  await db.daily_briefings.put({
    date: 'latest',
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
  return result.payload;
}

async function fetchAndStoreBriefing() {
  const cached = await db.meta.get('topic-briefing');
  const result = await getBriefingConditional({ etag: cached?.etag ?? null, limit: 10, include_misc: true });
  const now = Date.now();
  if (result.status === 304) {
    await db.meta.update('topic-briefing', { fetched_at: now });
    return cached?.payload ?? null;
  }
  if (result.payload === null) return null;
  const hash = await contentHash(result.payload);
  await db.meta.put({
    key: 'topic-briefing',
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
  return result.payload;
}

async function fetchAndStoreArticle(id) {
  const cached = await db.articles.get(id);
  const result = await getArticleConditional(id, { etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.articles.update(id, { fetched_at: now });
    return;
  }
  if (result.payload === null) return;
  const hash = await contentHash(result.payload);
  await db.articles.put({
    id,
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
    last_opened_at: cached?.last_opened_at ?? null,
    is_read: result.payload.is_read ? 1 : 0,
  });
}

// Returns the stored payload (fresh or cached) so the caller can collect article IDs.
async function fetchAndStoreTopicDigest(topicId) {
  const cached = await db.topic_digests.get(topicId);
  const result = await getTopicDigestConditional(topicId, { etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.topic_digests.update(topicId, { fetched_at: now });
    return cached?.payload ?? null;
  }
  if (result.payload === null) return null;
  const hash = await contentHash(result.payload);
  await db.topic_digests.put({
    topic_id: topicId,
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
  return result.payload;
}

async function fetchAndStoreHighlights() {
  const cached = await db.meta.get('all-highlights');
  const result = await getAllHighlightsConditional({ page: 1, per_page: 50, etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.meta.update('all-highlights', { fetched_at: now });
    return;
  }
  if (result.payload === null) return;
  const hash = await contentHash(result.payload);
  await db.meta.put({
    key: 'all-highlights',
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
}

function done(progress) {
  return { ...progress, phase: 'done' };
}

/**
 * Run the full prefetch graph: daily → digests → articles → highlights.
 *
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal] - Optional abort signal for lifecycle cleanup.
 * @param {Function} [opts.onProgress] - Called with progress object on each state change.
 * @returns {Promise<{phase:'done', fetched:number, total:number, errors:number}>}
 */
export async function runPrefetch({ signal, onProgress } = {}) {
  const progress = { phase: 'daily', fetched: 0, total: 0, errors: 0 };
  const emit = () => onProgress?.({ ...progress });
  emit();

  // Phase 1: daily briefing
  let dailyPayload = null;
  try {
    dailyPayload = await fetchAndStoreDaily();
  } catch {
    progress.errors++;
  }
  if (signal?.aborted) return done(progress);

  // Phase 2: unified topic briefing
  progress.phase = 'digests';
  emit();
  let briefingPayload = null;
  try {
    briefingPayload = await fetchAndStoreBriefing();
  } catch {
    progress.errors++;
  }
  if (signal?.aborted) return done(progress);

  // Phase 2b: individual topic digests (populates db.topic_digests for TopicDetail offline)
  const topicIds = (briefingPayload?.digests || []).map(d => d.topic_id).filter(Boolean);
  const topicDigestPayloads = [];
  await mapWithConcurrency(2, topicIds, async (topicId) => {
    if (signal?.aborted) return;
    try {
      const payload = await fetchAndStoreTopicDigest(topicId);
      if (payload) topicDigestPayloads.push(payload);
    } catch {
      progress.errors++;
    }
  }, { signal });
  if (signal?.aborted) return done(progress);

  // Phase 3: articles
  const ids = await collectArticleIds(dailyPayload, briefingPayload, topicDigestPayloads);
  progress.phase = 'articles';
  progress.total = ids.size;
  progress.fetched = 0;
  emit();

  await mapWithConcurrency(2, [...ids], async (id) => {
    if (signal?.aborted) return;
    try {
      await fetchAndStoreArticle(id);
    } catch {
      progress.errors++;
    }
    progress.fetched++;
    emit();
  }, { signal });

  if (signal?.aborted) return done(progress);

  // Phase 4: highlights
  progress.phase = 'highlights';
  emit();
  try {
    await fetchAndStoreHighlights();
  } catch {
    progress.errors++;
  }

  progress.phase = 'done';
  emit();
  return { ...progress };
}

/**
 * Request persistent storage once; records the result in meta['storage_persist'].
 * Idempotent — returns the cached result on subsequent calls.
 *
 * @returns {Promise<{granted:boolean, denied:boolean, alreadyAsked:boolean}>}
 */
export async function requestPersistOnce() {
  const row = await db.meta.get('storage_persist');
  if (row) {
    return {
      granted: !!row.value?.granted,
      denied: !row.value?.granted,
      alreadyAsked: true,
    };
  }
  if (!navigator?.storage?.persist) {
    await db.meta.put({ key: 'storage_persist', value: { granted: false, asked_at: Date.now() } });
    return { granted: false, denied: true, alreadyAsked: false };
  }
  const granted = await navigator.storage.persist();
  await db.meta.put({ key: 'storage_persist', value: { granted, asked_at: Date.now() } });
  return { granted, denied: !granted, alreadyAsked: false };
}

// Exported for unit tests only.
export const __test__ = { mapWithConcurrency, collectArticleIds, fetchAndStoreTopicDigest };
